<script setup lang="ts">
import { legalState } from '../legal';
import { pwa, promptInstall } from '../pwa';
import { changelog, showChangelog } from '../changelog';
import { feedback } from '../feedback';
import ProfileAvatar from './ProfileAvatar.vue';
import { profileState } from '../profiles/state';
import { displayName } from '../profiles/registry';
import { switchProfile } from '../profiles/session';
import { canLock, lockProfile } from '../profiles/lock';
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useApp, type View } from '../stores/app';
import { t, locale } from '../i18n';
import Icon from './Icon.vue';

/** Slide-out navigation drawer shared by all screens. */
const app = useApp();
const panel = ref<HTMLElement | null>(null);
const scrim = ref<HTMLElement | null>(null);
let returnFocus: HTMLElement | null = null;
let holeObserver: ResizeObserver | null = null;
const version = __APP_VERSION__;
/** Switch and lock sit under the profile card and stay folded until asked. */
const actionsOpen = ref(false);

/** Keep the dimmed overlay's cutout the same size as the glass, so the lens refracts the page rather than the scrim. */
function syncHole(): void {
  const h = panel.value?.offsetHeight;
  if (h) scrim.value?.style.setProperty('--drawer-h', `${h}px`);
}

const sections: { view: View; icon: string; label: string; beta?: boolean }[] = [
  { view: 'home', icon: 'grid', label: 'app.myCanvases' },
];

watch(
  () => app.drawerOpen,
  async (open) => {
    holeObserver?.disconnect();
    holeObserver = null;
    if (open) {
      returnFocus = document.activeElement as HTMLElement | null;
      await nextTick();
      syncHole();
      if (panel.value) {
        holeObserver = new ResizeObserver(syncHole);
        holeObserver.observe(panel.value);
      }
      (panel.value?.querySelector('button.item') as HTMLElement | null)?.focus();
    } else returnFocus?.focus?.();
  },
);

onBeforeUnmount(() => holeObserver?.disconnect());

function close(): void {
  app.drawerOpen = false;
}

function isActive(v: View): boolean {
  return app.view === v;
}

function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    e.stopPropagation();
    close();
    return;
  }
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
  const items = [...(panel.value?.querySelectorAll<HTMLElement>('button.item') ?? [])];
  const i = items.indexOf(document.activeElement as HTMLElement);
  items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
  e.preventDefault();
}
</script>

<template>
  <Transition name="fade">
    <div v-if="app.drawerOpen" ref="scrim" class="scrim" @click="close"></div>
  </Transition>
  <Transition name="slide">
    <nav v-if="app.drawerOpen" ref="panel" class="drawer glass" :aria-label="t('app.menu')" @keydown="onKey">
      <div class="body">
      <header>
        <div class="brand">
          <svg width="24" height="24" viewBox="0 0 32 32" aria-hidden="true">
            <rect x="3" y="3" width="26" height="26" rx="7" fill="var(--accent)" />
            <rect x="9" y="9" width="8" height="8" rx="1.5" fill="var(--on-accent)" />
            <circle cx="21" cy="21" r="4" fill="var(--on-accent)" />
          </svg>
          <span>{{ t('app.title') }}</span>
        </div>
        <button class="icon-btn" :aria-label="t('common.close')" @click="close"><Icon name="close" :size="16" /></button>
      </header>

      <div v-if="profileState.current && !profileState.guest" class="group profile-group">
        <div class="who-row">
          <button type="button" class="who" data-act="open-profile" :class="{ active: isActive('profile') }" :aria-current="isActive('profile') ? 'page' : undefined" @click="app.go('profile')">
            <ProfileAvatar :id="profileState.current.id" :first-name="profileState.current.firstName" :last-name="profileState.current.lastName" :avatar="profileState.current.avatar" :size="30" />
            <span class="who-name" data-field="profile-name">{{ displayName(profileState.current) }}</span>
            <span class="beta">{{ t('app.beta') }}</span>
          </button>
          <button type="button" class="who-more" data-act="profile-more" :aria-expanded="actionsOpen" :aria-label="t('profiles.actions')" @click="actionsOpen = !actionsOpen">
            <span class="chev" :class="{ open: actionsOpen }"><Icon name="chevronDown" :size="16" /></span>
          </button>
        </div>
        <template v-if="actionsOpen">
          <button class="item" data-act="switch-profile" @click="switchProfile">
            <Icon name="user" :size="17" />
            <span>{{ t('profiles.switch') }}</span>
          </button>
          <button v-if="canLock()" class="item" data-act="lock-profile" @click="(app.drawerOpen = false), lockProfile()">
            <Icon name="lock" :size="17" />
            <span>{{ t('profiles.lockNow') }}</span>
          </button>
        </template>
      </div>

      <div class="group">
        <button v-for="s in sections" :key="s.view" class="item" :class="{ active: isActive(s.view) }" :aria-current="isActive(s.view) ? 'page' : undefined" @click="app.go(s.view)">
          <Icon :name="s.icon" :size="17" />
          <span class="label">{{ t(s.label) }}<span v-if="s.beta" class="beta">{{ t('app.beta') }}</span></span>
        </button>
        <button class="item" :class="{ active: isActive('settings') }" :aria-current="isActive('settings') ? 'page' : undefined" @click="app.go('settings')">
          <Icon name="settings" :size="17" />
          <span class="label">{{ t('app.settings') }}</span>
        </button>
      </div>
      <div v-if="changelog.entries.length || feedback.email" class="group">
        <button v-if="changelog.entries.length" class="item" data-act="whats-new" @click="(app.drawerOpen = false), showChangelog()">
          <Icon name="info" :size="17" />
          <span>{{ t('changelog.menu') }}</span>
        </button>
        <button v-if="feedback.email" class="item" data-act="feedback" @click="(app.drawerOpen = false), (feedback.open = true)">
          <Icon name="comment" :size="17" />
          <span>{{ t('feedback.menu') }}</span>
        </button>
      </div>
      <div v-if="pwa.installable" class="group">
        <button class="item" data-act="install-app" @click="promptInstall()">
          <Icon name="installApp" :size="17" />
          <span>{{ t('pwa.install') }}</span>
        </button>
      </div>

      <footer class="muted">
        <span v-if="!pwa.online" class="offline" :title="t('pwa.offlineHint')">{{ t('pwa.offlineBadge') }}</span>
        <span>v{{ version }}</span>
        <a v-for="d in legalState.docs" :key="d.kind" :href="d.url" target="_blank" rel="noopener noreferrer">{{ locale === 'ru' ? d.title.ru : d.title.en }}</a>
      </footer>
      </div>
    </nav>
  </Transition>
</template>

<style scoped>
.scrim {
  position: fixed;
  inset: 0;
  z-index: 900;
  background: transparent;
}
/* Dim the page around the pane, leaving a hole so the glass samples the real content. */
.scrim::before {
  content: '';
  position: fixed;
  top: calc(var(--banner-h, 0px) + 10px);
  left: 10px;
  width: 260px;
  height: var(--drawer-h, 280px);
  border-radius: var(--radius-panel);
  box-shadow: 0 0 0 100vmax var(--backdrop);
  pointer-events: none;
}
.drawer {
  position: fixed;
  /* below the news banner, when there is one */
  top: calc(var(--banner-h, 0px) + 10px);
  left: 10px;
  width: 260px;
  /* as tall as its content: no empty column below the sections */
  max-height: calc(100% - 20px - var(--banner-h, 0px));
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-panel);
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.16);
  z-index: 901;
}
.body {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 0;
  overflow: auto;
  padding: 12px;
}
header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 2px 8px 6px;
}
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 14px;
}
.who-row {
  display: flex;
  align-items: center;
  gap: 2px;
}
.who {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  width: auto;
  padding: 6px 10px;
  border: none;
  background: none;
  border-radius: var(--radius-panel);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.who:hover {
  background: color-mix(in srgb, var(--text) 8%, transparent);
  color: var(--text);
}
.who.active {
  background: color-mix(in srgb, var(--accent) 18%, transparent);
  color: var(--accent-text);
}
.who-more {
  flex: none;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  border-radius: var(--radius-panel);
  color: var(--text-3);
  cursor: pointer;
  display: grid;
  place-items: center;
}
.who-more:hover {
  background: color-mix(in srgb, var(--text) 8%, transparent);
  color: var(--text);
}
.chev {
  display: grid;
  transition: transform 0.15s ease;
}
.chev.open {
  transform: rotate(180deg);
}
.who-name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.group {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 0;
}
.group + .who {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px 8px;
}
.who-name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.group {
  border-top: 1px solid color-mix(in srgb, var(--border) 65%, transparent);
}
.caption {
  margin: 4px 12px 6px;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-3);
  letter-spacing: 0.02em;
}
.item {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 40px;
  padding: 0 14px;
  border: none;
  background: none;
  border-radius: var(--radius-panel);
  color: var(--text-2);
  cursor: pointer;
  text-align: left;
  font-size: 13.5px;
}
.item:hover {
  background: color-mix(in srgb, var(--text) 8%, transparent);
  color: var(--text);
}
.item.active {
  background: color-mix(in srgb, var(--accent) 18%, transparent);
  color: var(--accent-text);
  font-weight: 500;
}
.label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.beta {
  margin-left: 0;
  padding: 1px 7px;
  border-radius: var(--radius-control);
  background: color-mix(in srgb, var(--accent) 16%, transparent);
  color: var(--accent-text);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.03em;
}
header .icon-btn:hover {
  background: color-mix(in srgb, var(--text) 8%, transparent);
  color: var(--text);
}
footer {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding: 8px 14px 2px;
  border-top: 1px solid color-mix(in srgb, var(--border) 65%, transparent);
  font-size: 11px;
}
.offline {
  padding: 1px 8px;
  border-radius: var(--radius-control);
  background: color-mix(in srgb, var(--warn) 18%, transparent);
  color: var(--text);
}
footer a {
  color: inherit;
}
.slide-enter-active,
.slide-leave-active {
  /* Opacity below 1 would make this a backdrop root and the glass would stop seeing the page. */
  transition: transform 0.2s ease;
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(-24px);
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .slide-enter-active,
  .slide-leave-active,
  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }
}
:global(:root[data-theme='dark']) .drawer {
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
}
</style>
